import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  CircleCheck,
  Loader2,
  Lock,
  Trash2,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { StripePaymentMethodSetupForm } from '@/components/organisms/payments/StripePaymentMethodSetupForm';
import { PayabliPaymentMethodSetupForm } from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import {
  useCompletePublicContactPaymentMethodRequest,
  useCompletePublicContactPaymentMethodTokenize,
  useCreatePublicContactPaymentMethodSetupIntent,
  usePublicContactPaymentMethodRequest,
} from '@/hooks/contacts/useContactPaymentMethods';
import { ApiError } from '@/lib/api/errors';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  PaymentMethodPageHeading,
  PaymentMethodPageMessage,
  PaymentMethodPageShell,
  type PaymentMethodTrustPoint,
} from '@/components/public/PaymentMethodPageShell';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const REPLACED_CARD_PROBLEM = {
  expired: 'has expired',
  declined: 'was declined',
  update: 'needs to be updated',
} as const;

export const Route = createFileRoute('/(public)/payment-method-request/$token')({
  component: ContactPaymentMethodRequestPage,
});

function ContactPaymentMethodRequestPage() {
  const { token } = Route.useParams();
  const requestQuery = usePublicContactPaymentMethodRequest(token);
  const setupIntent = useCreatePublicContactPaymentMethodSetupIntent(token);
  const completeRequest = useCompletePublicContactPaymentMethodRequest(token);
  const completeTokenize = useCompletePublicContactPaymentMethodTokenize(token);
  const [completed, setCompleted] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [authorizationAccepted, setAuthorizationAccepted] = useState(false);
  const [signedName, setSignedName] = useState('');
  const setupIntentRequested = useRef(false);

  // Payabli links mount the embedded component from the config in the request
  // details — no Stripe setup intent. Only the Stripe path mints one.
  const isPayabli = requestQuery.data?.provider === 'PAYABLI';
  const payabliConfig = requestQuery.data?.payabli;

  useEffect(() => {
    if (requestQuery.isSuccess && !isPayabli && !setupIntentRequested.current) {
      setupIntentRequested.current = true;
      setupIntent.mutate();
    }
  }, [requestQuery.isSuccess, isPayabli, setupIntent]);

  if (requestQuery.isLoading) {
    return (
      <PaymentMethodPageMessage className="max-w-5xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-48 w-full" />
      </PaymentMethodPageMessage>
    );
  }

  if (requestQuery.isError || !requestQuery.data) {
    // A 410 means the link was already used or replaced (single-use); anything
    // else is an invalid/expired link. Mirror the wording on the `/s/{code}`
    // landing page so the customer sees a consistent, specific message.
    const alreadyUsed = requestQuery.error instanceof ApiError && requestQuery.error.status === 410;
    return (
      <PaymentMethodPageMessage>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {alreadyUsed ? 'This link has already been used' : 'Invalid payment request link'}
          </AlertTitle>
          <AlertDescription>
            {alreadyUsed
              ? 'This payment link has already been used or replaced. Ask the office to send a new one.'
              : 'This request link is invalid or has expired. Ask the office to send a new one.'}
          </AlertDescription>
        </Alert>
      </PaymentMethodPageMessage>
    );
  }

  const data = requestQuery.data;
  const companyName = data.companyName || 'the company';
  // A replacement link: the card entered here takes the old card's place, and
  // any scheduled charges authorized on the old card are signed again for it.
  const replacing = data.replacing ?? null;
  const authorizations = data.authorizations ?? [];
  // Ticked AND signed with a typed name, the standard a renewal
  // re-authorization already holds the customer to.
  const authorizationPending =
    authorizations.length > 0 && !(authorizationAccepted && signedName.trim());
  const authorizationAnswer =
    authorizations.length > 0 ? { authorizationAccepted, signedName: signedName.trim() } : {};
  const submitLabel = replacing ? 'Replace card' : 'Save payment method';
  const holderLabel = replacing ? 'Name on card' : 'Cardholder / account holder name';
  const holderPlaceholder = replacing ? 'Name on card' : 'Name on card or bank account';
  // Saving from either kind of link charges nothing: the card is stored, and a
  // replacement only takes over the old card's row.
  const trustPoints: PaymentMethodTrustPoint[] = [
    {
      icon: CircleCheck,
      title: 'Nothing is charged today',
      body: replacing
        ? 'Your new card takes the old one’s place.'
        : 'This only saves the method on file.',
    },
    {
      icon: Lock,
      title: 'Encrypted and stored securely',
      body: 'Only the last 4 digits show after saving.',
    },
    authorizations.length > 0
      ? {
          icon: CalendarClock,
          title: 'Your scheduled payments carry on',
          body: 'Sign below and they move to the new card, with the same limits.',
        }
      : {
          icon: Trash2,
          title: 'You stay in control',
          body: `Ask ${companyName} to remove it any time.`,
        },
  ];
  // What the heading offers follows the rails the business accepts.
  // Fail closed: an absent flag disables the rail rather than re-opening it.
  // A replacement is a card for a card, its authorization and wording are all
  // about a card, so it never offers the bank rail. The server mints these
  // links card-only too; this keeps the page from depending on that.
  const acceptCard = data.acceptCard === true;
  const acceptACH = !replacing && data.acceptACH === true;
  const savingWhat =
    acceptCard && acceptACH ? 'a card or bank account' : acceptACH ? 'a bank account' : 'a card';

  const authorizationBox =
    !completed && authorizations.length > 0 ? (
      <div
        className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100"
        data-testid="replacement-authorization"
      >
        <label className="flex items-start gap-3">
          <Checkbox
            checked={authorizationAccepted}
            onCheckedChange={(checked) => setAuthorizationAccepted(checked === true)}
            className="mt-0.5"
          />
          <span className="space-y-1">
            <span className="block">
              I authorize {companyName} to charge my new card for the scheduled payments I
              authorized on my {replacing?.description ?? 'old card'}.
            </span>
            {authorizations.map((authorization, index) => (
              <span key={index} className="block text-amber-900/80 dark:text-amber-100/70">
                Maximum per charge: {currency.format(authorization.maxAmountPerCharge)}
                {authorization.maxTotal != null
                  ? `; total cap: ${currency.format(authorization.maxTotal)}`
                  : ''}
                .
              </span>
            ))}
          </span>
        </label>
        <div className="space-y-1.5">
          <Label htmlFor="authorization-signature">Type your full name to sign</Label>
          <Input
            id="authorization-signature"
            value={signedName}
            onChange={(event) => setSignedName(event.target.value)}
            placeholder="Full name"
            autoComplete="name"
            className="h-11 bg-white dark:bg-[#161513]"
          />
        </div>
      </div>
    ) : null;

  return (
    <PaymentMethodPageShell
      company={data}
      eyebrow="Payment method requested by"
      trustPoints={trustPoints}
      expiresAt={data.expiresAt}
      timezone={data.timezone}
    >
      <PaymentMethodPageHeading title={replacing ? 'Replace your card' : 'Add a payment method'}>
        {replacing ? (
          `Your ${replacing.description} ${REPLACED_CARD_PROBLEM[replacing.problem]}. Enter a new card to replace it.`
        ) : (
          <>
            Save {savingWhat} for{' '}
            <span className="font-medium text-stone-900 dark:text-stone-100">
              {data.contactName}
            </span>
            .
          </>
        )}
      </PaymentMethodPageHeading>

      {completed ? (
        <div className="space-y-4 py-8 text-center">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-950/50 dark:text-green-400">
              <CheckCircle2 className="h-8 w-8" />
            </div>
          </div>
          <div>
            <p className="text-lg font-semibold">
              {replacing ? 'Card updated' : 'Payment method saved'}
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-400">You can close this window.</p>
          </div>
        </div>
      ) : isPayabli ? (
        payabliConfig?.publicToken && payabliConfig.entryPoint && (acceptCard || acceptACH) ? (
          // No name field of our own: the embedded component asks for the
          // name on the card or account itself, and the server reads that
          // name back from the stored method.
          <PayabliPaymentMethodSetupForm
            appearance="customer"
            holderName={data.contactName}
            publicToken={payabliConfig.publicToken}
            entryPoint={payabliConfig.entryPoint}
            mode={payabliConfig.mode}
            submitLabel={(rail) =>
              replacing ? 'Replace card' : rail === 'ach' ? 'Save bank account' : 'Save card'
            }
            disabled={authorizationPending}
            beforeSubmit={authorizationBox}
            // Offer ONLY the rails the business accepts — the component
            // enables both unless told otherwise. Fail closed: an absent
            // flag disables the rail rather than re-opening it. The
            // tokenize endpoint enforces the same rails server-side.
            methods={{ card: acceptCard, ach: acceptACH }}
            // Paypoints that require a customer identifier reject
            // tokenization without a payor; customerNumber must be the
            // contact id (the backend exchange binds the same id).
            customerData={{
              customerNumber: data.contactId,
              firstName: data.contactName.split(' ')[0] || undefined,
              lastName: data.contactName.split(' ').slice(1).join(' ') || undefined,
            }}
            onComplete={async (result) => {
              await completeTokenize.mutateAsync({
                temporaryToken: result.temporaryToken,
                type: result.type,
                last4: result.last4 ?? '',
                brand: result.brand ?? null,
                expMonth: result.expMonth ?? null,
                expYear: result.expYear ?? null,
                ...authorizationAnswer,
              });
              setCompleted(true);
            }}
          />
        ) : (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to start secure card entry</AlertTitle>
            <AlertDescription>
              This payment link isn&apos;t configured for card capture. Ask the office to send a new
              one.
            </AlertDescription>
          </Alert>
        )
      ) : setupIntent.isPending ? (
        <div className="flex items-center justify-center rounded-xl border border-stone-200 p-10 dark:border-[#3a3835]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : setupIntent.data ? (
        <div className="space-y-5">
          {authorizationBox}
          <div className="space-y-2">
            <Label htmlFor="cardholder-name">{holderLabel}</Label>
            <Input
              id="cardholder-name"
              value={cardholderName}
              onChange={(event) => setCardholderName(event.target.value)}
              placeholder={holderPlaceholder}
              className="h-11"
            />
          </div>
          <StripePaymentMethodSetupForm
            clientSecret={setupIntent.data.clientSecret}
            publishableKey={setupIntent.data.publishableKey}
            stripeAccountId={setupIntent.data.stripeAccountId}
            submitLabel={submitLabel}
            disabled={authorizationPending}
            onComplete={async (paymentMethodId) => {
              // No `providerCustomerId`: the backend resolves the contact's
              // own Stripe customer, and a `cus_…` round-tripped through an
              // unauthenticated page is a claim about whose card this is —
              // one the server never acts on. No `provider` either; the
              // handler forces STRIPE.
              //
              // The backend still ACCEPTS both (deprecated, ignored, and
              // logged) purely so a cached copy of this bundle does not 400
              // mid-card-save. That tolerance is for old bundles, not for
              // this one — do not add them back, or the
              // `public-add-card legacy-field` warnings never reach zero and
              // the fields can never be removed.
              await completeRequest.mutateAsync({
                providerPaymentMethodId: paymentMethodId,
                cardholderName: cardholderName.trim() || null,
                ...authorizationAnswer,
              });
              setCompleted(true);
            }}
          />
        </div>
      ) : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to start secure card entry</AlertTitle>
          <AlertDescription>
            {setupIntent.error?.message ?? 'Secure card entry couldn’t start. Please try again.'}
          </AlertDescription>
        </Alert>
      )}
    </PaymentMethodPageShell>
  );
}
